import type { IconName } from '@customerio/pluma-icons';
import androidAsset from '@/assets/images/integrations/android.svg';
import logomarkJourneysAsset from '@/assets/images/logos/logomark-journeys.svg';
import s3Asset from '@/assets/images/integrations/s3.svg';
import azureBlobStorageAsset from '@/assets/images/integrations/azure-blob-storage.svg';
import redshiftAsset from '@/assets/images/integrations/redshift.svg';
import bigqueryAsset from '@/assets/images/integrations/bigquery.svg';
import expoAsset from '@/assets/images/integrations/expo.svg';
import flutterAsset from '@/assets/images/integrations/flutter.svg';
import goAsset from '@/assets/images/integrations/go.svg';
import logomarkDataPipelinesAsset from '@/assets/images/logos/logomark-data-pipelines.svg';
import appleAsset from '@/assets/images/integrations/apple.svg';
import javascriptAsset from '@/assets/images/integrations/javascript.svg';
import nodejsAsset from '@/assets/images/integrations/nodejs.svg';
import mysqlAsset from '@/assets/images/integrations/mysql.svg';
import postgresqlAsset from '@/assets/images/integrations/postgresql.svg';
import reactAsset from '@/assets/images/integrations/react.svg';
import pythonAsset from '@/assets/images/integrations/python.svg';
import segmentAsset from '@/assets/images/integrations/segment.svg';
import twilioAsset from '@/assets/images/integrations/twilio.svg';
import sqlserverAsset from '@/assets/images/integrations/sqlserver.svg';
import snowflakeAsset from '@/assets/images/integrations/snowflake.svg';
import salesforceAsset from '@/assets/images/integrations/salesforce.svg';
import googleAnalyticsAsset from '@/assets/images/integrations/google-analytics.svg';
import googleAdsAsset from '@/assets/images/integrations/google-ads.svg';
import googleSheetsAsset from '@/assets/images/integrations/google-sheets.svg';
import googleCloudStorageAsset from '@/assets/images/integrations/google-cloud-storage.svg';
import { IntegrationType } from './enums';
import { getStaticIntegrations } from './static-integrations';
import type { StaticIntegration } from './types';

export const PIPELINES_INTEGRATIONS_IMAGE_URLS: Record<string, string> = {
  android: androidAsset,
  customerio: logomarkJourneysAsset,
  'amazon-simple-storage-service': s3Asset,
  'azure-blob-storage': azureBlobStorageAsset,
  redshift: redshiftAsset,
  'amazon-redshift': redshiftAsset,
  bigquery: bigqueryAsset,
  'google-bigquery': bigqueryAsset,
  'google-cloud-storage': googleCloudStorageAsset,
  expo: expoAsset,
  flutter: flutterAsset,
  go: goAsset,
  http: logomarkDataPipelinesAsset,
  ios: appleAsset,
  javascript: javascriptAsset,
  node: nodejsAsset,
  mysql: mysqlAsset,
  postgres: postgresqlAsset,
  reactnative: reactAsset,
  python: pythonAsset,
  segment: segmentAsset,
  'segment-profiles': segmentAsset,
  twilio: twilioAsset,
  'twilio-studio': twilioAsset,
  'engage-messaging-twilio': twilioAsset,
  sqlserver: sqlserverAsset,
  snowflake: snowflakeAsset,
  salesforce: salesforceAsset,
  'google-analytics-4': googleAnalyticsAsset,
  'google-analytics-4-web': googleAnalyticsAsset,
  'google-ads': googleAdsAsset,
  'google-enhanced-conversions': googleAdsAsset,
  'google-sheets-non-etl': googleSheetsAsset,
};

export const PIPELINES_INTEGRATIONS_ICONS: Record<string, IconName> = {
  webhook: 'webhook',
};

export const PopularIntegrations = {
  salesforce: 'Salesforce',
  bigquery: 'BigQuery',
  redshift: 'Redshift',
  snowflake: 'Snowflake',
  postgres: 'PostgreSQL',
  mysql: 'MySQL',
  javascript: 'JavaScript',
  ios: 'iOS',
  android: 'Android',
  reactNative: 'React Native',
  flutter: 'Flutter',
  node: 'Node.js',
  python: 'Python',
  go: 'Go',
  jotform: 'Jotform',
  typeform: 'Typeform',
  facebookLeadAds: 'Facebook Lead Ads',
  http: 'Customer.io API',
} as const;
export type PopularIntegration = (typeof PopularIntegrations)[keyof typeof PopularIntegrations];

export type PipelinesImageOrIcon = {
  imageSrc?: string;
  icon?: IconName;
  title: string;
};

export function getPipelinesIntegrationImageSrcOrIcon(id: string, type?: IntegrationType): PipelinesImageOrIcon {
  const noPrefixId = id.replace(`pipelines_${type}_`, '');
  const imageSrc = PIPELINES_INTEGRATIONS_IMAGE_URLS[noPrefixId];
  const icon = PIPELINES_INTEGRATIONS_ICONS[noPrefixId];

  let title: string;
  if (noPrefixId in PopularIntegrations) {
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    title = PopularIntegrations[noPrefixId as keyof typeof PopularIntegrations];
  } else {
    title = noPrefixId !== '' ? noPrefixId : 'Unknown Integration';
  }

  return { imageSrc, icon, title };
}

export type PopularIntegrationListItem = {
  imageSrc?: string;
  icon?: string;
  title?: string;
  name: string;
  path: string;
  dynamicSegment?: string;
  isJourneysUiPath?: boolean;
};

export function createPopularIntegrationsList(
  workspaceId: string | undefined,
  filter: PopularIntegration[],
): PopularIntegrationListItem[] {
  const metadata = getStaticIntegrations();
  // oxlint-disable-next-line local-rules/enforce-generate-path -- cross-app Pipelines (CDP) deep-link, not a Hydra router path
  const url = (id: string) => `/workspaces/${workspaceId}/pipelines/sources/add?source=${id}`;
  const image = (id: string) => getPipelinesIntegrationImageSrcOrIcon(id, IntegrationType.pipelinesSource);
  const integration = (id: string, name: PopularIntegration): PopularIntegrationListItem => ({
    ...image(id),
    name,
    path: url(id),
    isJourneysUiPath: false,
  });
  const forms = (staticIntegration: StaticIntegration): PopularIntegrationListItem => {
    const { imageSrc, name, dynamicSegment } = staticIntegration;
    return {
      imageSrc,
      name,
      dynamicSegment,
      path: 'integrations.forms.type',
    };
  };

  const integrations: PopularIntegrationListItem[] = [
    integration('salesforce', PopularIntegrations.salesforce),
    integration('bigquery', PopularIntegrations.bigquery),
    integration('redshift', PopularIntegrations.redshift),
    integration('snowflake', PopularIntegrations.snowflake),
    integration('postgres', PopularIntegrations.postgres),
    integration('mysql', PopularIntegrations.mysql),
    integration('javascript', PopularIntegrations.javascript),
    integration('ios', PopularIntegrations.ios),
    integration('android', PopularIntegrations.android),
    integration('reactnative', PopularIntegrations.reactNative),
    integration('flutter', PopularIntegrations.flutter),
    integration('node', PopularIntegrations.node),
    integration('python', PopularIntegrations.python),
    integration('go', PopularIntegrations.go),
    forms(metadata.jotform),
    forms(metadata.typeform),
    forms(metadata.facebookLeadAds),
    integration('http', PopularIntegrations.http),
  ];
  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  return integrations.filter((item) => filter.includes(item.name as PopularIntegration));
}
